import {
  addDays, BEREAVEMENT_LIMIT_HOURS, BEREAVEMENT_RELATIONSHIPS, decidedByHr, KIND_LABEL, lastDayOnOrAfter, RELATIONSHIP_LABEL, requestLabel, requestRef,
  VOLUNTEER_HOURS, VOTE_HOURS, VOTE_WINDOW_LABEL, VOTE_WINDOWS,
  type BereavementRelationship, type VoteWindow, type Evaluation, type MyRequest, type PersonRef, type PersonSummary, type ProjectOption, type RequestType,
} from "@scheduler/shared";
import { useEffect, useMemo, useState } from "react";

import { ApiError, useSchedulerApi, type Draft } from "../api/scheduler";
import { changeStart } from "./change";
import { TYPE_HINT, TYPE_LABEL } from "./format";
import { ProjectPicker } from "./ProjectPicker";
import { WorkingPanel } from "./WorkingPanel";

const BLOCKS = [2, 4, 6, 8] as const;
/**
 * Which types a screen offers. Field work lives on its own screen now — a crew,
 * a location and a title have nothing in common with two hours off on Tuesday,
 * and one form switching between them served neither well.
 */
const TYPES: RequestType[] = ["PTO", "NTD"];

/** The kinds an employee may mark. Unpaid is HR's, on the HR tab. */
const EMPLOYEE_KINDS = ["JURY_DUTY", "BEREAVEMENT", "VOLUNTEER", "VOTE"] as const;
type EmployeeKind = (typeof EMPLOYEE_KINDS)[number];

const days = (h: number) => `${h / 8} ${h === 8 ? "day" : "days"}`;

/** Next Monday, as YYYY-MM-DD, in the browser's own calendar. */
function nextMonday(): string {
  const now = new Date();
  const iso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const dow = now.getDay();
  return addDays(iso, ((8 - dow) % 7) || 7);
}

/**
 * Submit PTO, a no-travel day, or a field deployment. Every change re-runs the
 * gates on the API (preview, which writes nothing) so the working is always
 * current; submit runs them again under the per-person lock.
 */
/**
 * `refreshKey` changes whenever requests change elsewhere (a submit or a cancel), so the working is re-checked.
 *
 * With `replacing` the form CHANGES that request (punch list S35, 3 Nov 2026): it starts filled in
 * from it (change.ts) and the save replaces it through the reschedule rule. The starting state is
 * read once, on mount, so the parent mounts a fresh form per request (a `key`).
 */
export function NewRequest({ me, refreshKey, onSubmitted, only, replacing }: {
  me: PersonSummary;
  refreshKey: number;
  onSubmitted: (message: string) => void;
  /** Restrict the form to one type. The field screen passes FIELD. */
  only?: RequestType;
  /** The request this form changes, if any. */
  replacing?: MyRequest;
}) {
  const offered = only ? [only] : TYPES;
  const api = useSchedulerApi();
  const change = useMemo(() => (replacing ? changeStart(replacing) : null), [replacing]);
  const [type, setType] = useState<RequestType>(only ?? change?.type ?? (me.ptoAccru === "0" ? "NTD" : "PTO"));
  const [start, setStart] = useState(() => change?.start ?? nextMonday());
  const [end, setEnd] = useState(() => change?.end ?? addDays(nextMonday(), 4));
  const [crew, setCrew] = useState<string[]>([me.id]);
  const [title, setTitle] = useState("");
  const [explanation, setExplanation] = useState(change?.explanation ?? "");
  const [partial, setPartial] = useState(change?.partial ?? false);
  const [perDay, setPerDay] = useState<Record<string, number>>(() => ({ ...(change?.perDay ?? {}) }));
  // "Other kind of time off?" — collapsed until asked for (TIME_OFF_PLAN, screens 1); open when changing one.
  const [kindOpen, setKindOpen] = useState(change?.kind != null);
  const [kind, setKind] = useState<EmployeeKind | null>(change?.kind ?? null);
  const [relationship, setRelationship] = useState<BereavementRelationship | null>(change?.relationship ?? null);
  // Vote only, and required (HR, 12 Oct 2026): the start or the end of the day.
  const [voteWindow, setVoteWindow] = useState<VoteWindow | null>(change?.voteWindow ?? null);
  const [people, setPeople] = useState<PersonRef[]>([]);
  const [projects, setProjects] = useState<ProjectOption[] | null>(null);
  const [projectsFailed, setProjectsFailed] = useState(false);
  const [projectKey, setProjectKey] = useState<number | null>(null);
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (type !== "FIELD" || people.length) return;
    api.people().then(setPeople, (e: unknown) => setError((e as Error).message));
  }, [api, type, people.length]);

  useEffect(() => {
    if (type !== "FIELD" || projects || projectsFailed) return;
    // A failure says "contact HR" in the picker rather than leaving it loading forever.
    api.projects().then(setProjects, () => setProjectsFailed(true));
  }, [api, type, projects, projectsFailed]);

  const project = projects?.find((p) => p.ajeraKey === projectKey) ?? null;

  // Working days in the range, as the API counted them (holidays live on the server). Keyed by
  // content, not identity: every preview returns a fresh object, and re-previewing on identity
  // alone would loop.
  const workingKey = evaluation?.breakdown?.days.filter((d) => d.kind === "WORKING").map((d) => d.date).join(",") ?? "";
  const workingDays = useMemo(() => (workingKey ? workingKey.split(",") : []), [workingKey]);

  const draft: Draft | null = useMemo(() => {
    if (!start || !end) return null;
    // Changing a request: for the same people, and naming the request it replaces (rule 16).
    const base = {
      type, start, end,
      resourceIds: type === "FIELD" ? crew : change ? [...change.resourceIds] : [me.id],
      ...(change ? { supersedesRequestId: change.requestId } : {}),
    };
    if (type === "FIELD") {
      // The API refuses FIELD without a project and says so; sending what was chosen lets the
      // working panel show that message rather than the form guessing it.
      const withProject = projectKey !== null ? { ...base, projectAjeraKey: projectKey } : base;
      return title.trim() ? { ...withProject, title: title.trim() } : withProject;
    }
    // The kind goes with the draft, so the working panel shows its check: no balance, its limit.
    const kinded = type === "PTO" && kind
      ? {
        ...base, timeOffKind: kind,
        ...(kind === "BEREAVEMENT" && relationship ? { bereavementRelationship: relationship } : {}),
        // A vote is one day, its fixed 2 h: the last day follows the first, and no partial days.
        ...(kind === "VOTE" ? { end: start, ...(voteWindow ? { voteWindow } : {}) } : {}),
      }
      : base;
    if (type === "PTO" && partial && workingDays.length && kind !== "VOTE") {
      return { ...kinded, perDay: Object.fromEntries(workingDays.map((d) => [d, perDay[d] ?? 8])) };
    }
    return kinded;
  }, [type, start, end, crew, title, projectKey, partial, perDay, workingDays, kind, relationship, voteWindow, me.id, change]);

  // Re-run the gates whenever the draft's content changes, debounced.
  const draftKey = draft ? JSON.stringify(draft) : "";
  useEffect(() => {
    if (!draftKey) return;
    const current = JSON.parse(draftKey) as Draft;
    let stale = false;
    setLoading(true);
    const timer = setTimeout(() => {
      api.preview(current).then(
        (e) => { if (!stale) { setEvaluation(e); setError(null); } },
        (e: unknown) => { if (!stale) setError(e instanceof ApiError ? e.message : "Could not check this request."); },
      ).finally(() => { if (!stale) setLoading(false); });
    }, 250);
    return () => { stale = true; clearTimeout(timer); };
  }, [api, draftKey, refreshKey]);

  async function submit() {
    if (!draft) return;
    setSubmitting(true);
    try {
      // Added at submit, not in the draft: the draft is re-previewed as it changes, and the
      // gates never read the explanation, so typing in it should not re-run them.
      const result = await api.submit({ ...draft, explanation: explanation.trim() || null });
      if (!result.ok) {
        setEvaluation(result.evaluation);
        return;
      }
      // Named by what it is and its dates, never its number (PR 9).
      const name = requestRef(requestLabel(draft.type, kind, true), draft.start, draft.end);
      const where = draft.type === "FIELD" ? "Field work" : "My requests";
      // A change approved on submit cancels the request it replaces at once (Approvals' words).
      const replaced = replacing && result.cancelled.includes(replacing.id)
        ? `; ${requestRef(replacing.label, replacing.start, replacing.end)} cancelled as replaced`
        : "";
      const message = result.status === "APPROVED"
        ? `${name} approved${replaced}. See it under ${where}.`
        : `Submitted — waiting on ${result.waitingOn ?? "HR"}. See it under ${where}.`;
      setPartial(false);
      setPerDay({});
      setKind(null);
      setRelationship(null);
      setVoteWindow(null);
      setKindOpen(false);
      setTitle("");
      setExplanation("");
      onSubmitted(message);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  }

  const blocked = !evaluation?.ok || loading;
  // Only the kinds HR decides go to HR (decidedByHr): volunteer and vote go to the supervisor, so no line.
  const routing = me.ptoApprovalAuto
    ? decidedByHr(kind) ? "Approved as soon as you submit, and HR is told" : "Approved as soon as you submit"
    : decidedByHr(kind)
      ? "Goes to HR to approve"
      : me.supervisor ? null : "No supervisor on record, so HR decides it";

  return (
    <div className="req-grid">
      <div className="card card-pad">
        <div className="field">
          <span className="label" id="type-label">Request type</span>
          <div className="typepick" role="group" aria-labelledby="type-label">
            {offered.map((t) => (
              <button key={t} type="button" className="typebtn" data-t={t} aria-pressed={type === t} onClick={() => setType(t)}>
                <span className="tname">{TYPE_LABEL[t]}</span>
                <span className="tsub">{TYPE_HINT[t]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* PTO only, and only when it does NOT simply go to the supervisor (PR 9): the accrual
            rate and "goes to <supervisor>" said what everybody already knows. What is left is
            the exception worth reading before submitting. */}
        {type === "PTO" && routing ? <p className="accrualnote">{routing}</p> : null}

        {type === "FIELD" ? (
          <>
            {/* Required (9 Oct 2026): the project's PM approves the booking and owns it after. */}
            <div className="field">
              <span className="label">Project</span>
              <ProjectPicker projects={projects} failed={projectsFailed} value={projectKey} onChange={setProjectKey} />
              {project ? (
                <span className="hint">
                  {project.projectManager
                    ? `${project.projectManager.name}, the project's PM, approves this booking.`
                    : "No PM on record for this project: only HR can approve or change this booking."}
                </span>
              ) : null}
            </div>
            <fieldset className="field">
              <legend className="label">Crew — {crew.length} assigned</legend>
              <div className="crewbox">
                {people.map((p) => (
                  <label key={p.id} className="crewrow">
                    <input
                      type="checkbox"
                      checked={crew.includes(p.id)}
                      onChange={(e) => setCrew(e.target.checked ? [...crew, p.id] : crew.filter((x) => x !== p.id))}
                    />
                    <span>{p.name}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="field">
              <span className="label">Deployment name</span>
              <input type="text" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
            </label>
          </>
        ) : (
          // A change is for the people on the request being changed (HR may change anyone's).
          <p className="hint">For <strong>{replacing ? replacing.people.map((p) => p.name).join(", ") : me.name}</strong>.</p>
        )}

        {/* Last day follows First day when it would otherwise be earlier, and its picker opens
            no earlier than First day (punch list S27, Jeremy's option A). A range typed in the
            other order is kept. A vote is one day, so its last day is always the first. */}
        <div className="row2">
          <label className="field">
            <span className="label">First day</span>
            <input type="date" value={start} onChange={(e) => {
              const first = e.target.value;
              setStart(first);
              if (kind === "VOTE") setEnd(first);
              else if (first) setEnd((last) => lastDayOnOrAfter(first, last));
            }} />
          </label>
          <label className="field">
            <span className="label">Last day</span>
            <input type="date" value={kind === "VOTE" ? start : end} min={start || undefined} disabled={kind === "VOTE"} onChange={(e) => setEnd(e.target.value)} />
          </label>
        </div>

        {type === "PTO" && kind !== "VOTE" ? (
          <div className="field">
            <label className="togglerow">
              <input type="checkbox" checked={partial} onChange={(e) => setPartial(e.target.checked)} />
              <span>
                <span className="tt">Partial days</span>
                <span className="ts">Take less than a full day. PTO comes in 2-hour blocks.</span>
              </span>
            </label>
            {partial ? (
              <div className="pdbox">
                {workingDays.length ? workingDays.map((d) => (
                  <div key={d} className="pday">
                    <span className="pdname">{d}</span>
                    <div className="pdbtns" role="group" aria-label={`Hours on ${d}`}>
                      {BLOCKS.map((h) => (
                        <button key={h} type="button" className="hbtn" aria-pressed={(perDay[d] ?? 8) === h}
                          onClick={() => setPerDay({ ...perDay, [d]: h })}>{h}h</button>
                      ))}
                    </div>
                  </div>
                )) : <p className="muted">No working days in this range to split.</p>}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Another kind of time off: PTO with a kind. No balance check, and nobody outside the
            visibility rule sees which it was. HR decides jury duty and bereavement; volunteer and
            vote go to the supervisor. Not offered to part-timers (H4); the API refuses them
            regardless. */}
        {type === "PTO" && me.ptoAccru !== "0" ? (
          <div className="field">
            <button type="button" className="ghost" aria-expanded={kindOpen} onClick={() => setKindOpen(!kindOpen)}>
              {kindOpen ? "▾" : "▸"} Other kind of time off?
            </button>
            {kindOpen ? (
              <div className="pdbox">
                <div className="kindpick" role="group" aria-label="Kind of time off">
                  {EMPLOYEE_KINDS.map((k) => (
                    <button key={k} type="button" className="hbtn" aria-pressed={kind === k}
                      onClick={() => {
                        setKind(kind === k ? null : k); setRelationship(null); setVoteWindow(null);
                        if (k === "VOTE" && kind !== k) { setEnd(start); setPartial(false); }
                      }}>{KIND_LABEL[k]}</button>
                  ))}
                  {kind ? <button type="button" className="ghost" onClick={() => { setKind(null); setRelationship(null); setVoteWindow(null); }}>Clear</button> : null}
                </div>
                {kind === "BEREAVEMENT" ? (
                  <label className="field">
                    <span className="label">Who was it?</span>
                    <select value={relationship ?? ""} onChange={(e) => setRelationship((e.target.value || null) as BereavementRelationship | null)}>
                      <option value="">Choose…</option>
                      {BEREAVEMENT_RELATIONSHIPS.map((r) => (
                        <option key={r} value={r}>{RELATIONSHIP_LABEL[r]}: up to {days(BEREAVEMENT_LIMIT_HOURS[r])}</option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {kind === "VOTE" ? (
                  <fieldset className="field">
                    <legend className="label">When will you vote? <span className="muted">(required)</span></legend>
                    <div className="kindpick" role="group" aria-label="When you will vote">
                      {VOTE_WINDOWS.map((w) => (
                        <button key={w} type="button" className="hbtn" aria-pressed={voteWindow === w} onClick={() => setVoteWindow(w)}>{VOTE_WINDOW_LABEL[w]}</button>
                      ))}
                    </div>
                  </fieldset>
                ) : null}
                <span className="hint">
                  {kind === "VOTE"
                    ? `${VOTE_HOURS} hours on one day, at the start or the end of it. `
                    : kind === "VOLUNTEER"
                      ? `Up to ${VOLUNTEER_HOURS} hours in any 365 days. `
                      : ""}
                  None of these uses your PTO balance.{" "}
                  {me.ptoApprovalAuto
                    ? "Yours is approved on submit."
                    : kind === "VOLUNTEER" || kind === "VOTE"
                      ? "Your supervisor approves it."
                      : "HR approves jury duty and bereavement, not your supervisor."}
                  {" "}Others see only "Time Off".
                </span>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Optional on every type, never required (9 Oct). Private: the API
            returns it only to the people on the request, whoever decides it,
            HR and project managers, and never puts it on the calendar or in a
            notification. The hint says so, because people write differently
            when they know who reads it. */}
        <label className="field">
          <span className="label">Explanation <span className="muted">(optional)</span></span>
          <textarea
            rows={3}
            maxLength={2000}
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
          />
          <span className="hint">Seen only by you, your approver, HR and PMs. Not shown on the team calendar.</span>
        </label>

        {error ? <p className="error" role="alert">{error}</p> : null}

        <button type="button" className="primary" disabled={blocked || submitting} onClick={() => void submit()}>
          {submitting ? "Submitting…" : "Submit request"}
        </button>
      </div>

      <WorkingPanel evaluation={evaluation} loading={loading} approverName={me.supervisor?.name ?? null} type={type} />
    </div>
  );
}
